import { CombinedGraphQLErrors } from '@apollo/client';

/** `extensions.code` of Go's refusal of an undeliverable e-mail domain. */
export const EMAIL_UNDELIVERABLE = 'EMAIL_UNDELIVERABLE';

export const STRONG_AUTH_MESSAGE =
  'This action requires passkey authentication (amr: passkey). A magic-link session cannot be upgraded — sign in with a passkey.';

function firstGraphQLError(error: unknown): {
  message: string;
  code?: string;
  extensions?: Record<string, unknown>;
} | null {
  if (CombinedGraphQLErrors.is(error)) {
    const first = error.errors[0];
    if (!first) return null;
    const code =
      first.extensions && typeof first.extensions === 'object' && 'code' in first.extensions
        ? String(first.extensions.code)
        : undefined;
    const extensions =
      first.extensions && typeof first.extensions === 'object'
        ? (first.extensions as Record<string, unknown>)
        : undefined;
    return { message: first.message, code, extensions };
  }
  if (error instanceof Error) {
    return { message: error.message };
  }
  return null;
}

/** The GraphQL `extensions.code` of the first error (FORBIDDEN, BAD_USER_INPUT, …), if any. */
export function graphqlErrorCode(error: unknown): string | undefined {
  return firstGraphQLError(error)?.code;
}

/**
 * The corrected address of an `EMAIL_UNDELIVERABLE` refusal
 * (`extensions.suggestion`), when Go offered one. Undefined for any other error.
 */
export function graphqlErrorSuggestion(error: unknown): string | undefined {
  const gql = firstGraphQLError(error);
  if (gql?.code !== EMAIL_UNDELIVERABLE) return undefined;
  const suggestion = gql.extensions?.suggestion;
  return typeof suggestion === 'string' && suggestion ? suggestion : undefined;
}

/** The refused domain of an `EMAIL_UNDELIVERABLE` error (`extensions.domain`), if present. */
export function graphqlErrorDomain(error: unknown): string | undefined {
  const gql = firstGraphQLError(error);
  if (gql?.code !== EMAIL_UNDELIVERABLE) return undefined;
  const domain = gql.extensions?.domain;
  return typeof domain === 'string' && domain ? domain : undefined;
}

/** `extensions.reason` of the first error (e.g. `DOORS_WINDOW_REQUIRED`), if any. */
export function graphqlErrorReason(error: unknown): string | undefined {
  const reason = firstGraphQLError(error)?.extensions?.reason;
  return typeof reason === 'string' ? reason : undefined;
}

export function isStrongAuthError(error: unknown): boolean {
  const gql = firstGraphQLError(error);
  if (!gql) return false;
  if (gql.code === 'FORBIDDEN' && /requires passkey authentication/i.test(gql.message)) {
    return true;
  }
  return /requires passkey authentication/i.test(gql.message);
}

export function isNotImplementedError(error: unknown): boolean {
  const gql = firstGraphQLError(error);
  if (!gql) return false;
  return /not implemented|panic/i.test(gql.message);
}

/** Human-readable GraphQL / network error, with a specific LoA (1.5) message. */
export function graphqlErrorMessage(
  error: unknown,
  fallback = 'Something went wrong.',
): string {
  if (isStrongAuthError(error)) {
    return STRONG_AUTH_MESSAGE;
  }
  const gql = firstGraphQLError(error);
  if (gql?.message) return gql.message;
  return fallback;
}
